import React, { useEffect, useState } from 'react';

import axios from 'axios';

import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import { useForm } from 'react-hook-form';

import DeptSelect from 'components/Inputs/DeptSelect';
import ExcelUpload from 'components/FileUpload/ExcelUpload';
import { cmmUtils } from 'utils/cmmUtils.js';

const columns = [
  { id: 'rowNumber', label: 'No', minWidth: 40, align: 'center' },
  { id: 'id', label: 'ID', minWidth: 50, align: 'center' },
  { id: 'product', label: '제품', minWidth: 120, align: 'center' },
  { id: 'title', label: '제목', minWidth: 300, align: 'left' },
  { id: 'command', label: '명령어', minWidth: 100, align: 'center' },
  { id: 'deptsNm', label: '허용 부서', minWidth: 220, align: 'left' },
  { id: 'useYnNm', label: '사용여부', minWidth: 80, align: 'center' }
];

// 한 칸에 여러 부서를 담기 위한 구분자. 쉼표는 부서명 자체에 들어갈 수 있어 쓰지 않는다.
// 서버(tip.dao.js)의 TARGET_SEPARATOR 와 같은 값이어야 한다.
const TARGET_SEPARATOR = ';';

// 엑셀 컬럼. id 가 엑셀 2행(영문명)에 찍히고 업로드 때 그대로 서버 필드명이 되므로
// 서버가 받는 이름(depts/useYn)과 일치시켜 둔다.
// 허용 부서 칸은 부서명으로 내보내지만, 올릴 때는 부서코드나 부문명으로 적어도 받아준다.
const EXCEL_COLUMNS = [
  { id: 'id', label: 'ID' },
  { id: 'product', label: '제품' },
  { id: 'title', label: '제목' },
  { id: 'body', label: '내용' },
  { id: 'command', label: '명령어' },
  { id: 'depts', label: '허용 부서' },
  { id: 'useYn', label: '사용여부' }
];

const EXCEL_FILE_NAME = 'TIP 관리';

const EXCEL_WARNING = `업로드한 파일 내용으로 TIP 전체가 교체됩니다.
파일에 없는 TIP 은 삭제되므로, 반드시 [엑셀 다운로드] 받은 파일을 고쳐서 올려주세요.
ID 를 비우면 신규 등록됩니다.
허용 부서는 ; 로 구분하고, 비우면 모든 부서에 표시됩니다.
부서코드로 적어도 되고, 부문명을 적으면 그 부문 소속 부서 전체로 펼쳐집니다.`;

// 허용 부서가 비어 있으면 전체 공개라 엑셀에서도 빈 칸으로 둔다.
// 목록 화면과 달리 '전체' 라고 적으면 그 이름의 부서를 찾게 되므로 쓰지 않는다.
const toExcelRow = (row) => ({
  id: row.id,
  product: row.product || '',
  title: row.title || '',
  body: row.body || '',
  command: row.command || '',
  depts: (row.depts || []).join(TARGET_SEPARATOR),
  useYn: row.useYn === false ? '미사용' : '사용중'
});

export default function TipMng() {
  const [srchData, setSrchData] = useState({});
  const [datas, setDatas] = useState({ useYn: true });
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [depts, setDepts] = useState([]);
  const [openExcel, setOpenExcel] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    mode: 'all'
  });

  const handleChangeSrch = (e) => {
    let id = (e.target.id || e.target.name).replace('Srch', '');
    setSrchData({ ...srchData, [id]: e.target.value });
  };

  const handleChange = (e) => {
    const id = (e.target.id || e.target.name || '').replace(/^datas\./, '');
    setDatas({ ...datas, [id]: e.target.value });
  };

  // 허용 부서는 register 를 쓰지 않으므로 useYn 체크박스와 같은 방식으로 다룬다.
  const handleChangeDepts = (depts) => {
    setDatas({ ...datas, depts });
    setValue('datas.depts', depts, { shouldDirty: true });
  };

  const handleChangeUseYn = (e) => {
    const checked = e.target.checked;
    setDatas({ ...datas, useYn: checked });
    setValue('datas.useYn', checked, { shouldDirty: true });
  };

  const handleRowClick = (data) => (evt) => {
    initForm();
    setDatas(data);
    setValue('datas', data);
  };

  // 제품 목록은 제품관리(pgm_mng)에서 가져온다.
  // engNm 이 'DHCadOn_2024' 처럼 접미사를 가질 수 있어 '_' 앞 접두어만 중복 제거해 쓴다.
  const getProductList = async () => {
    const response = await axios.get(`/api/pgm`);
    if (Array.isArray(response.data)) {
      setProducts(Array.from(new Set(response.data.map((r) => (r.engNm || '').split('_')[0]).filter((v) => !!v))));
    }
  };

  // 부문/부서 원장은 DIDAS 가 갖고 있고, 서버가 캐시해서 넘겨준다.
  // DIDAS 를 직접 부르지 않는 건 화면과 서버가 같은 원장을 보게 하려는 것이다.
  const getSectorDeptList = async () => {
    try {
      const response = await axios.get(`/api/tip/dept-info`);
      const data = response?.data ?? {};
      setSectors(Array.isArray(data.sectorList) ? data.sectorList : []);
      setDepts(Array.isArray(data.deptList) ? data.deptList : []);
    } catch (e) {
      // 부문/부서를 못 받아와도 팁 등록·수정 자체는 되어야 하므로 목록만 비워 둔다.
      console.log('부문/부서 목록 조회 실패', e);
    }
  };

  // 페이징 없이 조건에 맞는 팁을 전부 받아온다.
  const getDataList = async (e) => {
    const { product, title, command } = srchData;
    let params = { product, title, command };

    const response = await axios.get(`/api/tip`, { params });
    if (response.data) {
      setRows(response.data.rows);
    }
  };

  const onClickInit = (evt) => {
    initForm();
  };

  // 검색 조건이 걸린 상태로 내려받은 뒤 그대로 올리면 나머지 팁이 전부 지워진다(업로드는 전체 교체).
  const onClickExcelDown = () => {
    cmmUtils.excelDownload(rows.map(toExcelRow), EXCEL_FILE_NAME, EXCEL_COLUMNS, true);
  };

  const onClickExcelUp = () => {
    if (srchData?.product || srchData?.title || srchData?.command) {
      alert('검색 조건이 걸려 있으면 일부 TIP 만 담긴 파일을 받게 됩니다.\r\n검색을 초기화한 뒤 다시 받아서 올려주세요.');
    }
    setOpenExcel(true);
  };

  const handleCloseExcel = () => setOpenExcel(false);

  // 엑셀의 부문/부서 검증은 서버(tip.dao.js)에서 한다.
  // 코드·이름을 다 받아주고 엑셀이 '020' 을 20 으로 바꿔놓는 것까지 감안해야 하는데,
  // 그 규칙을 화면에도 한 벌 두면 둘이 어긋나는 순간 멀쩡한 파일이 여기서 막힌다.

  const initForm = () => {
    setDatas({ useYn: true });
    clearErrors();
    reset();
  };

  const TipMngCreate = async (data) => {
    return await axios.post(`/api/tip`, data);
  };

  const TipMngUpdate = async (data) => {
    return await axios.patch(`/api/tip/${data.id}`, data);
  };

  const onClickDelete = async (evt) => {
    try {
      if (datas?.id) {
        await axios
          .delete(`/api/tip/${datas.id}`)
          .then((response) => {
            getDataList();
            initForm();
            alert(response.data.message);
          })
          .catch((error) => {
            alert(error.response.data.message);
          });
      }
    } catch (e) {
      console.log(`insert failed`);
    }
  };

  const createOrUpdate = async (datas) => {
    if (!!datas?.id) {
      return await TipMngUpdate(datas);
    } else {
      return await TipMngCreate(datas);
    }
  };

  const onSubmit = (data) => {
    if (isDirty) {
      // 체크박스와 멀티셀렉트는 register 스프레드를 쓰지 않으므로 로컬 상태값을 합쳐 보낸다.
      createOrUpdate({ ...data.datas, useYn: datas?.useYn !== false, depts: datas?.depts || [] })
        .then((response) => {
          getDataList();
          initForm();
          alert(response.data.message);
        })
        .catch((error) => {
          console.log(error);
          alert(error.response.data.message);
        });
    }
  };

  useEffect(() => {
    getProductList();
    getSectorDeptList();
    getDataList();
  }, []);

  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
      <Typography component="h2" variant="h6" color="primary" gutterBottom>
        TIP 관리
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Stack direction="row" spacing={2}>
            <TextField id="productSrch" name="productSrch" select variant="outlined" size="small" label="제품" sx={{ minWidth: 160 }} value={srchData?.product || ''} onChange={handleChangeSrch}>
              <MenuItem value="">전체</MenuItem>
              {products.map((product) => (
                <MenuItem key={product} value={product}>
                  {product}
                </MenuItem>
              ))}
            </TextField>
            <TextField id="titleSrch" variant="outlined" size="small" label="제목" value={srchData?.title || ''} onChange={handleChangeSrch} />
            <TextField id="commandSrch" variant="outlined" size="small" label="명령어" value={srchData?.command || ''} onChange={handleChangeSrch} />
            <Button variant="contained" onClick={getDataList}>
              검색
            </Button>
            <Button variant="outlined" onClick={onClickExcelDown} disabled={rows.length === 0}>
              엑셀 다운로드
            </Button>
            <Button variant="outlined" onClick={onClickExcelUp}>
              엑셀 업로드
            </Button>
          </Stack>
          {/* 페이지 전체가 스크롤되면 오른쪽 폼까지 같이 움직이므로, 스크롤은 목록 영역 안에서만 생기게 한다. */}
          <TableContainer component={'div'} sx={{ maxHeight: 'calc(100vh - 260px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth, bgcolor: 'background.paper' }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIdx) => (
                  <TableRow hover key={row.id} onClick={handleRowClick(row)}>
                    {columns.map((col, colIdx) => {
                      return (
                        <TableCell key={`row${rowIdx}-col${colIdx}`} align={col.align}>
                          {row[col.id]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        {/* 목록 영역만 스크롤되므로 폼은 제자리에 머문다. 창 높이가 짧을 때를 대비해 sticky 는 유지한다. */}
        <Grid item xs={3} sx={{ position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" onClick={onClickInit}>
              초기화
            </Button>
            <Button variant="contained" type="submit" form="TipMngForm" disabled={isDirty && JSON.stringify(datas) !== '{}' ? false : true}>
              {!!datas?.id ? '수정' : '등록'}
            </Button>
            <Button variant="contained" onClick={onClickDelete} disabled={datas?.id ? false : true}>
              삭제
            </Button>
          </Stack>
          <Box id="TipMngForm" component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1, minWidth: 300, maxWidth: 500 }}>
            <TextField
              margin="dense"
              required
              fullWidth
              select
              name="product"
              label="제품"
              id="product"
              value={datas?.product || ''}
              error={!!errors.datas?.product}
              helperText={errors.datas?.product && errors.datas?.product.message}
              {...register('datas.product', {
                required: '제품을 선택해주세요.',
                onChange: (e) => handleChange(e)
              })}
            >
              {products.map((product) => (
                <MenuItem key={product} value={product}>
                  {product}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              required
              fullWidth
              name="title"
              label="제목"
              id="title"
              value={datas?.title || ''}
              error={!!errors.datas?.title}
              helperText={errors.datas?.title && errors.datas?.title.message}
              {...register('datas.title', {
                required: '제목을 입력해주세요.',
                onChange: (e) => handleChange(e)
              })}
            />
            <TextField
              margin="dense"
              required
              fullWidth
              multiline
              rows={6}
              name="body"
              label="내용"
              id="body"
              value={datas?.body || ''}
              error={!!errors.datas?.body}
              helperText={errors.datas?.body && errors.datas?.body.message}
              {...register('datas.body', {
                required: '내용을 입력해주세요.',
                onChange: (e) => handleChange(e)
              })}
            />
            <TextField
              margin="dense"
              fullWidth
              name="command"
              label="명령어"
              id="command"
              value={datas?.command || ''}
              error={!!errors.datas?.command}
              helperText={errors.datas?.command && errors.datas?.command.message}
              {...register('datas.command', {
                onChange: (e) => handleChange(e)
              })}
            />
            {/* 하나도 안 고르면 전체 공개. 부문 체크는 하위 부서를 한 번에 켜고 끄는 용도다. */}
            <DeptSelect sectors={sectors} depts={depts} value={datas?.depts || []} onChange={handleChangeDepts} />
            <FormControlLabel control={<Checkbox checked={datas?.useYn !== false} onChange={handleChangeUseYn} />} label="사용여부" sx={{ mt: 1 }} />
          </Box>
        </Grid>
      </Grid>
      <ExcelUpload open={openExcel} url={`/api/tip/excel`} sampleFileName={EXCEL_FILE_NAME} rows={rows.map(toExcelRow)} columns={EXCEL_COLUMNS} columnNm={true} warning={EXCEL_WARNING} onSaved={getDataList} handleClose={handleCloseExcel} />
    </Paper>
  );
}
